import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { scrollUp, BasicLayout } from '@monorepo/common';

import FinalBlock from './components/FinalBlock';
import ListingHeaderBand from './components/ListingHeaderBand';
import CrossBrandBanner from '../../components/CrossBrandBanner';
import { MetaDescription } from '../../components/MetaDescription';

import { showNotification } from '../../features/sliceNotification';
import { useFreePaymentRequestMutation, usePaymentRequestMutation } from '../../services/servicePostAJob';

import { JobStepper } from '../../components/JobStepper';
import { useAppSelector } from '../../store/hooks';
import { setPageTitle } from '../../utils/setPageTitle';
import { cloudfrontImagesBaseURL } from '../../components/JobStepper/staticValues';
import { Hosts } from '../../constants/hostName';
import { gtmPush } from '../../utils/gtm';
import { hsIdentify, hsPush } from '../../utils/hubspot';
import { useGetUserInfoQuery } from '../../services/serviceUserInfo';

// interface IJobsProps {}
export const PostJobListing = () => {
  const { data: userInfo } = useGetUserInfoQuery();
  const dispatch = useDispatch();
  const { siteNumber, sourcePathname } = useAppSelector(({ pathnameSettings }) => ({
    siteNumber: pathnameSettings.siteNumber,
    sourcePathname: pathnameSettings.pathname
  }));

  // Only reassure visitors who arrived from a named brand board (/nosh,
  // /brewbound, /bevnet). The bare /PostJobListing entry has no source brand,
  // so the cross-brand strip is suppressed there.
  const showCrossBrandBanner = Boolean(sourcePathname);

  const [titleBrowserPage, setTitleBrowserPage] = useState<string>('Jobs');
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [paymentRequest, paidResult] = usePaymentRequestMutation();
  const [freePaymentRequest, freeResult] = useFreePaymentRequestMutation();
  const data = freeResult.data || paidResult.data;
  const isPaymentLoading = paidResult.isLoading || freeResult.isLoading;
  const finalResponseError = freeResult.error || paidResult.error;
  const finalResponseSuccess = freeResult.isSuccess || paidResult.isSuccess;

  const finalPaymentRequest = (formData: { body: any; id: string; validateOnly: boolean; isFree?: boolean }) => {
    const request = formData.isFree ? freePaymentRequest : paymentRequest;
    return request({
      body: {
        ...formData.body,
        step5: {
          ...formData.body.step5,
          uploadedLogoPath: formData.body.step5.uploadedLogoPath?.replace(cloudfrontImagesBaseURL, '')
        }
      },
      id: siteNumber.toString(),
      validateOnly: formData.validateOnly
    });
  };

  useEffect(() => {
    if (finalResponseError && 'data' in finalResponseError) {
      const resp = finalResponseError as { data: { error: string } };
      scrollUp();
      dispatch(showNotification({
        show: true,
        type: 'error',
        text: resp.data.error
      }));
    }
    if (finalResponseSuccess) {
      setIsComplete(true);
      // GA4 conversion events. post_job_complete always fires; purchase only for
      // paid orders (prepaid-slot posts produce no order/amount).
      gtmPush({ event: 'post_job_complete', site_id: data?.siteId ?? null, job_slug: data?.path ?? '' });
      const amount = Number(data?.orderAmount ?? 0);
      if (data?.orderId && amount > 0) {
        gtmPush({ ecommerce: null }); // clear previous ecommerce object (GA4/GTM convention)
        gtmPush({
          event: 'purchase',
          ecommerce: {
            transaction_id: String(data.orderId),
            value: amount,
            currency: 'USD',
            items: [{ item_name: 'Job Listing', item_category: 'cpgjobs' }]
          }
        });
      }
      // Tie the poster to their HubSpot contact (posting runs on a logged-in
      // account) and flush — identify only sends with the next tracked event.
      if (userInfo?.email) {
        hsIdentify(userInfo.email, {
          firstname: userInfo.firstName || undefined,
          lastname: userInfo.lastName || undefined,
        });
        hsPush('trackPageView');
      }
      dispatch(showNotification({
        show: true,
        type: 'success',
        text: 'Job was created!'
      }));
    }
  }, [finalResponseError, finalResponseSuccess]);

  useEffect(() => {
    if (isComplete) {
      const timeout = setTimeout(() => {
        setIsComplete(false);
        clearTimeout(timeout);
      }, 1000);
    }
  }, [isComplete]);

  useEffect(() => {
    setPageTitle({
      firstTitle: 'Post a Job Listing',
      siteNumber: siteNumber || Hosts.BEVNET,
      setTitle: setTitleBrowserPage
    });
  }, [siteNumber]);

  return (
    <BasicLayout
      dataCy="postJobListing"
      helmet={MetaDescription({ title: titleBrowserPage })}
    >
      {showCrossBrandBanner && <CrossBrandBanner />}
      <ListingHeaderBand />
      <JobStepper
        isComplete={isComplete}
        isStepperLoading={isPaymentLoading}
        finalStepperRequest={finalPaymentRequest}
        finalComponent={<FinalBlock data={data} siteId={siteNumber} />}
      />
    </BasicLayout>
  );
};
